/**
 * Signup Flow Resolver
 *
 * CANONICAL ROUTING RESOLVER — Single source of truth for signup/verify/onboarding flow.
 * Contract: SIGNUP_VERIFICATION_ONBOARDING_CONTRACT_V1.md v2.2.0 §4
 *
 * This resolver implements the deterministic routing decision table.
 * All guards and pages MUST use this resolver instead of inline redirect logic.
 */

/**
 * Input state for routing decisions
 */
export interface SignupFlowState {
  /** User is authenticated (auth.currentUser != null) */
  signedIn: boolean;
  /** Email verification satisfied (OAuth always true, password requires emailVerified) */
  verificationSatisfied: boolean;
  /** User has an active team selected with valid membership */
  hasActiveTeam: boolean;
  /** Onboarding completed for active team */
  onboardingCompleted: boolean;
  /** User has at least one team membership */
  hasAnyTeamMembership: boolean;
  /** Active team ID (if any) */
  activeTeamId?: string | null;
  /** Current path user is trying to access */
  currentPath?: string;
}

/**
 * Routing decision output
 */
export interface SignupFlowRouteDecision {
  /** Target route path, or null to allow current route */
  to: string | null;
  /** Whether to use replace navigation (prevents back button loops) */
  replace?: boolean;
  /** Debug/logging reason for this decision */
  reason: string;
}

/**
 * Helper: Check if path is a public or onboarding-only route
 * These routes should redirect to /dashboard for fully onboarded users
 */
function isPublicOrOnboardingRoute(path: string): boolean {
  const publicOrOnboardingPaths = [
    '/',
    '/login',
    '/signup',
    '/reset-password',
    '/verify-email',
    '/verify-complete',
    '/onboarding/create-team',
    '/onboarding/welcome',
    '/team-picker',
  ];

  // Exact match or starts with /onboarding/
  return publicOrOnboardingPaths.includes(path) || path.startsWith('/onboarding/');
}

/**
 * Canonical routing resolver for signup/verify/onboarding flow
 *
 * Decision table (evaluated in order):
 * 1. Not signed in → /login
 * 2. Signed in but no memberships → /onboarding/create-team (OAuth users without team)
 * 3. Signed in with team but not verified → /verify-email
 * 4. Signed in, verified, has memberships but no active team → /team-picker (or /settings)
 * 5. Signed in, verified, has active team but onboarding incomplete → /onboarding/welcome
 * 6. Signed in, verified, has active team, onboarding complete:
 *    - If on public/onboarding route → /dashboard
 *    - If on app route → null (allow access)
 *
 * @param state - Current user/team state
 * @returns Routing decision with target path (or null to allow) and reason
 */
export function resolveSignupFlowRoute(state: SignupFlowState): SignupFlowRouteDecision {
  // Case 1: Not signed in → login
  if (!state.signedIn) {
    return {
      to: '/login',
      replace: true,
      reason: 'Not signed in',
    };
  }

  // Case 2: Signed in but NOT verified → verify email FIRST
  // CONTRACT v2.0.0 §4: Verification MUST happen BEFORE team creation
  if (!state.verificationSatisfied) {
    return {
      to: '/verify-email',
      replace: true,
      reason: 'Email not verified - verification required before team creation',
    };
  }

  // Case 3: Verified but no team memberships → create team
  if (!state.hasAnyTeamMembership) {
    return {
      to: '/onboarding/create-team',
      replace: true,
      reason: 'No team memberships - need to create team',
    };
  }

  // Case 4: Verified but no active team (has memberships but activeTeamId not set)
  // Route to team picker if multiple teams, or settings to select active team
  if (state.verificationSatisfied && !state.hasActiveTeam && state.hasAnyTeamMembership) {
    // Check if team-picker route exists
    // TEMP FALLBACK: Use /team-picker if it exists, otherwise /settings
    // This handles the case where user has memberships but no activeTeamId selected
    return {
      to: '/team-picker',
      replace: true,
      reason: 'Has memberships but no active team - need to select team',
    };
  }

  // Case 5: Verified with active team but onboarding incomplete → onboarding
  if (state.verificationSatisfied && state.hasActiveTeam && !state.onboardingCompleted) {
    return {
      to: '/onboarding/welcome',
      replace: true,
      reason: 'Onboarding incomplete',
    };
  }

  // Case 6: Fully set up → main app
  if (state.verificationSatisfied && state.hasActiveTeam && state.onboardingCompleted) {
    // Only redirect to /dashboard from public/onboarding routes
    // Allow access to any app route (inventory, vehicles, etc.)
    const currentPath = state.currentPath || '/';

    if (isPublicOrOnboardingRoute(currentPath)) {
      return {
        to: '/dashboard',
        replace: false,
        reason: 'Fully onboarded - redirecting from public/onboarding route to dashboard',
      };
    }

    // User is on an app route - allow access
    return {
      to: null,
      replace: false,
      reason: 'Fully onboarded - allowing access to app route',
    };
  }

  // Fallback (should not reach here if state is valid)
  // If we do reach here, route to dashboard and let guards handle it
  return {
    to: '/dashboard',
    replace: false,
    reason: 'Fallback - unexpected state',
  };
}
